import { useState, useEffect, useMemo } from 'react';
import { Search, Filter, AlertCircle, TrendingUp, Target, MousePointer, X, Plus, Check, Loader2, ExternalLink, Settings } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import AucunCompteGoogleAds from './connectors/AucunCompteGoogleAds';

const API_URL = config.API_URL;

// Paramètres par défaut
const DEFAULT_SETTINGS = {
  period: 'LAST_90_DAYS',
  campaignType: 'ALL',
  minImpressions: 1,
  minClicks: 1,
  minConversions: 2,
  excludeNegatives: true,
  aggregateData: true
};

// Nom de l'outil pour la sauvegarde des paramètres
const TOOL_NAME = 'keywordOpportunities';

const KeywordOpportunitiesTool = ({ token, selectedClientId, updateUrlWithAccount }) => {
  // États pour les comptes
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // États pour les données et les filtres
  const [searchTermsData, setSearchTermsData] = useState([]);
  const [existingKeywords, setExistingKeywords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // État pour le modal de détails
  const [selectedTerm, setSelectedTerm] = useState(null);

  // États pour le modal d'ajout de mot-clé
  const [addKeywordTerm, setAddKeywordTerm] = useState(null);
  const [addMatchType, setAddMatchType] = useState('EXACT');
  const [addSelectedAdGroups, setAddSelectedAdGroups] = useState([]);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(null);
  const [availableAdGroups, setAvailableAdGroups] = useState([]);
  const [loadingAdGroups, setLoadingAdGroups] = useState(false);

  // États pour les paramètres
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [tierFilter, setTierFilter] = useState(null);

  // Charger les paramètres depuis l'API quand le compte change
  useEffect(() => {
    const loadSettings = async () => {
      if (!token || !selectedAccount) {
        setSettings(DEFAULT_SETTINGS);
        setSettingsLoaded(false);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/api/tools/settings/${TOOL_NAME}/${selectedAccount}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.success && response.data.settings) {
          setSettings({ ...DEFAULT_SETTINGS, ...response.data.settings });
        } else {
          setSettings(DEFAULT_SETTINGS);
        }
      } catch (e) {
        console.error('Erreur chargement paramètres:', e);
        setSettings(DEFAULT_SETTINGS);
      } finally {
        setSettingsLoaded(true);
      }
    };
    loadSettings();
  }, [token, selectedAccount]);

  // Sauvegarder les paramètres dans l'API quand ils changent
  useEffect(() => {
    if (!settingsLoaded || !token || !selectedAccount) return;

    const saveSettings = async () => {
      try {
        await axios.post(`${API_URL}/api/tools/settings/${TOOL_NAME}/${selectedAccount}`,
          { settings },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } catch (e) {
        console.error('Erreur sauvegarde paramètres:', e);
      }
    };

    const timeoutId = setTimeout(saveSettings, 500);
    return () => clearTimeout(timeoutId);
  }, [settings, settingsLoaded, token, selectedAccount]);

  // Vérifier si les paramètres sont personnalisés
  const hasCustomSettings = useMemo(() => {
    return Object.keys(DEFAULT_SETTINGS).some(key => {
      return String(DEFAULT_SETTINGS[key]) !== String(settings[key]);
    });
  }, [settings]);

  // État pour le tri des colonnes
  const [sortColumn, setSortColumn] = useState('conversions');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' ou 'desc'

  // Charger les comptes au montage et quand le client change
  useEffect(() => {
    if (token) {
      // Réinitialiser tous les états quand le client change
      setSelectedAccount('');
      setSearchTermsData([]);
      setExistingKeywords([]);
      setError(null);
      setSelectedTerm(null);
      setSettingsLoaded(false);
      // Recharger les comptes
      loadAccounts();
    }
  }, [token, selectedClientId]);

  // Charger les données quand le compte est sélectionné (sans dépendance sur les settings)
  useEffect(() => {
    if (token && selectedAccount) {
      fetchData();
    }
  }, [token, selectedAccount]);

  // Fonction pour charger la liste des comptes
  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      console.log('🔄 [KEYWORD-OPP] Chargement des comptes Google Ads...');
      
      const response = await axios.get(`${API_URL}/api/library?usage=outils`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ [KEYWORD-OPP] Réponse API:', response.data);
      
      if (response.data.success && response.data.library) {
        const data = response.data;
        
        // Combiner les comptes Google Ads depuis différentes sources (même logique que PortfolioStrategies)
        const googleSheetsItems = data.library?.GOOGLE_SHEETS || [];
        const googleAdsItems = data.library?.GOOGLE_ADS || [];
        
        // Filtrer pour ne garder que les comptes Google Ads
        const adsItems = [
          ...googleSheetsItems.filter(item => item.type === 'GOOGLE_ADS'),
          ...googleAdsItems.filter(item => item.type === 'GOOGLE_ADS')
        ];

        console.log(`📊 [KEYWORD-OPP] ${adsItems.length} compte(s) Google Ads trouvé(s)`);
        
        // Filtrer par client si selectedClientId est défini
        let filteredAccounts = adsItems;
        if (selectedClientId) {
          filteredAccounts = await filterAccountsByClient(adsItems, selectedClientId);
        }
        
        // Trier les comptes par ordre alphabétique
        const sortedAccounts = [...filteredAccounts].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
        );
        setAccounts(sortedAccounts);

        // Sélectionner automatiquement le premier compte s'il n'y en a qu'un
        if (sortedAccounts.length === 1) {
          const firstAccount = sortedAccounts[0];
          const accountId = firstAccount.sourceId || firstAccount.id;
          setSelectedAccount(accountId);
          console.log(`🎯 [KEYWORD-OPP] Sélection automatique: ${accountId}`);
        }
      } else {
        console.warn('⚠️ [KEYWORD-OPP] Réponse inattendue:', response.data);
        setError('Format de réponse inattendu pour les comptes');
      }
    } catch (error) {
      console.error('❌ [KEYWORD-OPP] Erreur chargement comptes:', error);
      setError('Impossible de charger les comptes Google Ads: ' + error.message);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Filtrer les comptes selon le client sélectionné (même logique que PortfolioStrategies)
  const filterAccountsByClient = async (accounts, clientId) => {
    if (!clientId) {
      return accounts; // Pas de filtrage si aucun client sélectionné
    }
    
    try {
      // Récupérer les sites du client pour obtenir les accountIds autorisés
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.sites) {
        const clientSites = data.sites.filter(site => site.clientId === clientId);
        const authorizedAccountIds = clientSites.flatMap(site => site.accountIds || []);
        
        console.log('🎯 [KEYWORD-OPP] Sites du client:', clientSites.length);
        console.log('🎯 [KEYWORD-OPP] IDs comptes autorisés:', authorizedAccountIds);
        
        // Filtrer les comptes
        const filteredAccounts = accounts.filter(account => {
          const matchesId = authorizedAccountIds.includes(account.id);
          const matchesSourceId = authorizedAccountIds.includes(account.sourceId);
          const isAuthorized = matchesId || matchesSourceId;
          
          if (isAuthorized) {
            console.log(`✅ [KEYWORD-OPP] Compte autorisé: ${account.name}`);
          }
          
          return isAuthorized;
        });
        
        console.log('🎯 [KEYWORD-OPP] Comptes filtrés:', filteredAccounts.length, '/', accounts.length);
        return filteredAccounts;
      }
    } catch (err) {
      console.error('❌ [KEYWORD-OPP] Erreur filtrage comptes:', err);
    }
    
    return accounts; // Retourner tous les comptes en cas d'erreur
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Récupérer les termes de recherche
      const searchTermsResponse = await axios.get(`${API_URL}/api/keyword-opportunities/search-terms`, {
        params: {
          clientId: selectedAccount,
          period: settings.period,
          campaignType: settings.campaignType,
          aggregateData: settings.aggregateData
        },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSearchTermsData(searchTermsResponse.data);

      // Récupérer les mots-clés existants
      const keywordsResponse = await axios.get(`${API_URL}/api/keyword-opportunities/existing-keywords`, {
        params: { clientId: selectedAccount },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setExistingKeywords(keywordsResponse.data.keywords || keywordsResponse.data || []);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Charger les groupes d'annonces pour le modal d'ajout
  const loadAdGroups = async () => {
    if (!selectedAccount) return;

    setLoadingAdGroups(true);
    try {
      const response = await axios.get(`${API_URL}/api/keyword-opportunities/ad-groups`, {
        params: { clientId: selectedAccount },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setAvailableAdGroups(response.data.adGroups || []);
      }
    } catch (err) {
      console.error('Erreur chargement groupes d\'annonces:', err);
    } finally {
      setLoadingAdGroups(false);
    }
  };

  // Ouvrir le modal d'ajout de mot-clé
  const openAddKeywordModal = (term) => {
    setAddKeywordTerm(term);
    setAddMatchType('EXACT');
    setAddSelectedAdGroups([]);
    setAddError(null);
    setAddSuccess(null);

    // Charger les groupes d'annonces si pas encore chargés
    if (availableAdGroups.length === 0) {
      loadAdGroups();
    }
  };

  // Extraire les groupes d'annonces détectés depuis le breakdown du terme
  const getDetectedAdGroups = () => {
    if (!addKeywordTerm?.breakdown) return [];

    // Extraire les infos uniques des groupes d'annonces du breakdown
    const detected = addKeywordTerm.breakdown
      .filter(b => b.adGroupId || b.adGroupName)
      .map(b => ({
        id: b.adGroupId,
        name: b.adGroupName || 'Groupe inconnu',
        campaignName: b.campaignName,
        campaignType: b.campaignType
      }));

    // Dédupliquer par id ou par nom si pas d'id
    const unique = [];
    const seen = new Set();
    detected.forEach(ag => {
      const key = ag.id || ag.name;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(ag);
      }
    });

    return unique;
  };

  // Obtenir les "autres" groupes d'annonces (pas dans le breakdown)
  const getOtherAdGroups = () => {
    const detectedIds = new Set(getDetectedAdGroups().map(ag => ag.id).filter(Boolean));
    return availableAdGroups.filter(ag => !detectedIds.has(ag.id));
  };

  // Fermer le modal d'ajout
  const closeAddKeywordModal = () => {
    setAddKeywordTerm(null);
    setAddError(null);
    setAddSuccess(null);
  };

  // Ajouter le mot-clé
  const handleAddKeyword = async () => {
    if (!addKeywordTerm || addSelectedAdGroups.length === 0) {
      setAddError('Veuillez sélectionner au moins un groupe d\'annonces');
      return;
    }

    setAddLoading(true);
    setAddError(null);
    setAddSuccess(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/keyword-opportunities/add-keyword`,
        {
          clientId: selectedAccount,
          keywords: [addKeywordTerm.searchTerm],
          matchType: addMatchType,
          adGroupIds: addSelectedAdGroups
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAddSuccess(`Mot-clé "${addKeywordTerm.searchTerm}" ajouté avec succès à ${response.data.created} groupe(s) d'annonces`);

        // Ajouter immédiatement le mot-clé à la liste locale pour qu'il disparaisse des opportunités
        // (sans attendre la synchronisation Google Ads qui peut prendre du temps)
        setExistingKeywords(prev => [
          ...prev,
          {
            text: addKeywordTerm.searchTerm.toLowerCase(),
            matchType: addMatchType,
            isNegative: false
          }
        ]);

        // Fermer le modal après 2 secondes
        setTimeout(() => {
          closeAddKeywordModal();
        }, 2000);
      } else {
        setAddError(response.data.error || 'Erreur lors de l\'ajout du mot-clé');
      }
    } catch (err) {
      setAddError(err.response?.data?.error || err.message || 'Erreur lors de l\'ajout du mot-clé');
    } finally {
      setAddLoading(false);
    }
  };

  // Basculer la sélection d'un groupe d'annonces
  const toggleAdGroupSelection = (adGroupId) => {
    setAddSelectedAdGroups(prev =>
      prev.includes(adGroupId)
        ? prev.filter(id => id !== adGroupId)
        : [...prev, adGroupId]
    );
  };

  // Analyser les opportunités basées sur les filtres
  const analyzeOpportunities = useMemo(() => {

    if (!searchTermsData.length) {
      return []; // Seuls les termes de recherche sont requis
    }

    // Fonction pour normaliser les accents et apostrophes (é→e, è→e, à→a, l'→l, etc.)
    const normalizeText = (str) => {
      return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Supprimer accents
        .replace(/[''`]/g, '') // Supprimer apostrophes (droites et courbes)
        .toLowerCase();
    };

    const filteredTerms = searchTermsData
      .filter(term => {
        // Filtres de performance
        const ctr = (term.clicks / term.impressions * 100);
        const cpa = term.conversions > 0 ? (term.cost / term.conversions) : 0;
        
        const meetsImpressions = term.impressions >= settings.minImpressions;
        const meetsClicks = term.clicks >= settings.minClicks;
        const meetsConversions = term.conversions >= settings.minConversions;

        if (!meetsImpressions || !meetsClicks || !meetsConversions) {
          return false;
        }

        // Vérifier si le terme n'existe pas déjà comme mot-clé
        const termLower = term.searchTerm.toLowerCase();
        const alreadyExists = existingKeywords
          .filter(keyword => !keyword.isNegative) // Exclure les mots-clés négatifs
          .some(keyword => {
            const keywordLower = keyword.text.toLowerCase();
            
            // NOUVELLE LOGIQUE STRICTE - uniquement correspondance exacte
            const exactMatch = keywordLower === termLower;
            
            // Debug temporaire pour vérifier le changement
            if (['volet roulant électrique', 'somfy volet roulant', 'store discount avis'].includes(termLower)) {
              console.log(`🔄 [NEW-LOGIC] "${term.searchTerm}" vs "${keyword.text}" = ${exactMatch ? 'MATCH' : 'NO MATCH'}`);
            }
            
            // Retourner SEULEMENT la correspondance exacte
            return exactMatch;
          });

        // Masquer les termes déjà achetés (correspondance exacte)
        // Ne montrer que les nouveaux termes et variantes proches
        return !alreadyExists;
      });

    
    return filteredTerms
      .map(term => {
        // Analyser le statut du terme par rapport aux mots-clés existants
        const termLower = term.searchTerm.toLowerCase();
        const relevantKeywords = existingKeywords.filter(keyword => !keyword.isNegative);
        
        // 1. Vérifier correspondance exacte
        const exactMatch = relevantKeywords.some(keyword => 
          keyword.text.toLowerCase() === termLower
        );
        
        // 2. Vérifier variantes proches si pas de correspondance exacte
        let isCloseVariant = false;
        if (!exactMatch) {
          isCloseVariant = relevantKeywords.some(keyword => {
            const keywordNorm = normalizeText(keyword.text);
            const termNorm = normalizeText(term.searchTerm);

            // Correspondance exacte sans accents (ex: "fenêtre" = "fenetre")
            if (keywordNorm === termNorm) {
              return true;
            }

            // Détection singulier/pluriel (incluant pluriels irréguliers)
            const pluralRules = [
              // Pluriels réguliers
              { singular: keywordNorm, plural: keywordNorm + 's' },
              { singular: termNorm, plural: termNorm + 's' },
              // Pluriels irréguliers courants (normalisés sans accents)
              { singular: 'bateau', plural: 'bateaux' },
              { singular: 'rideau', plural: 'rideaux' },
              { singular: 'volet', plural: 'volets' },
              { singular: 'store', plural: 'stores' },
              { singular: 'fenetre', plural: 'fenetres' },
              { singular: 'porte', plural: 'portes' }
            ];

            const isPlural = pluralRules.some(rule =>
              (keywordNorm === rule.singular && termNorm === rule.plural) ||
              (keywordNorm === rule.plural && termNorm === rule.singular) ||
              (keywordNorm.includes(rule.singular) && termNorm.includes(rule.plural)) ||
              (keywordNorm.includes(rule.plural) && termNorm.includes(rule.singular))
            ) ||
            // Fallback pour les pluriels réguliers non listés
            (
              (keywordNorm + 's' === termNorm) ||
              (termNorm + 's' === keywordNorm) ||
              (keywordNorm.endsWith('s') && keywordNorm.slice(0, -1) === termNorm) ||
              (termNorm.endsWith('s') && termNorm.slice(0, -1) === keywordNorm)
            );

            // Détection variantes mineures (mots supprimés/ajoutés)
            const keywordWords = keywordNorm.split(' ');
            const termWords = termNorm.split(' ');
            const isMinorVariant = (
              keywordWords.length >= 2 && termWords.length >= 2 &&
              (
                keywordWords.every(word => termWords.includes(word)) ||
                termWords.every(word => keywordWords.includes(word))
              ) &&
              Math.abs(keywordWords.length - termWords.length) <= 1
            );
            
            return isPlural || isMinorVariant;
          });
        }

        // Calcul du score d'opportunité
        const ctr = (term.clicks / term.impressions * 100);
        const conversionRate = term.conversions / term.clicks * 100;
        const cpa = term.conversions > 0 ? term.cost / term.conversions : 0;
        
        // Score basé sur CTR, taux de conversion et volume
        const ctrScore = Math.min(ctr / 10, 10); // Max 10 points
        const conversionScore = Math.min(conversionRate * 2, 10); // Max 10 points
        const volumeScore = Math.min(term.impressions / 1000, 10); // Max 10 points
        const costScore = cpa > 0 ? Math.max(10 - (cpa / 10), 0) : 5; // Max 10 points

        const totalScore = (ctrScore + conversionScore + volumeScore + costScore) / 4;

        // Estimation des impressions potentielles
        const competitionFactor = ctr > 5 ? 1.2 : 0.8; // Moins de concurrence si excellent CTR
        const volumeFactor = term.impressions > 1000 ? 1.1 : 0.9; // Bonus pour gros volume
        const matchTypeFactor = ctr > 5 ? 0.6 : 1.0; // Exact (CTR élevé) = scope plus restreint
        
        // Estimation conservative : 50% des impressions observées avec facteurs d'ajustement
        const potentialImpressions = Math.round(
          term.impressions * 0.5 * competitionFactor * volumeFactor * matchTypeFactor
        );

        // Classification par tier avec critères pondérés
        const scoreWeight = totalScore >= 7 ? 3 : 0;
        const ctrWeight = ctr >= 5 ? 2 : ctr >= 3 ? 1 : 0;
        const volumeWeight = term.impressions >= 1000 ? 2 : term.impressions >= 500 ? 1 : 0;
        const conversionWeight = term.conversions >= 3 ? 2 : term.conversions >= 1 ? 1 : 0;
        const cpaWeight = cpa > 0 && cpa <= 25 ? 2 : cpa <= 50 ? 1 : 0;
        
        const totalWeight = scoreWeight + ctrWeight + volumeWeight + conversionWeight + cpaWeight;
        
        let tier;
        if (totalWeight >= 8) tier = 'EXCELLENT';
        else if (totalWeight >= 6) tier = 'HIGH';
        else if (totalWeight >= 4) tier = 'MEDIUM';
        else tier = 'LOW';

        // Estimation ROI prédictif
        const estimatedClicks = potentialImpressions * (ctr / 100);
        const estimatedConversions = estimatedClicks * (conversionRate / 100);
        const suggestedBid = Math.max(term.avgCpc * 0.8, 0.50);
        const estimatedCost = estimatedClicks * suggestedBid;
        const estimatedRevenue = estimatedConversions * 50; // Valeur conversion moyenne estimée à 50€
        const predictedROI = estimatedRevenue > 0 ? (estimatedRevenue - estimatedCost) / estimatedCost : -1;

        return {
          ...term,
          ctr,
          conversionRate,
          cpa,
          opportunityScore: totalScore,
          potentialImpressions,
          tier,
          totalWeight,
          predictedROI,
          estimatedClicks: Math.round(estimatedClicks),
          estimatedConversions: Math.round(estimatedConversions * 10) / 10, // 1 décimale
          estimatedCost: Math.round(estimatedCost * 100) / 100, // 2 décimales
          estimatedRevenue: Math.round(estimatedRevenue * 100) / 100,
          suggestedBid,
          suggestedMatchType: ctr > 5 ? 'EXACT' : 'PHRASE',
          exactMatch,
          isCloseVariant,
          keywordStatus: exactMatch ? 'EXACT_MATCH' : (isCloseVariant ? 'CLOSE_VARIANT' : 'NEW_KEYWORD'),
          // Maintenir la compatibilité
          alreadyExists: exactMatch,
          opportunityType: exactMatch ? 'OPTIMIZATION' : 'NEW_KEYWORD'
        };
      })
      .sort((a, b) => b.conversions - a.conversions);
  }, [searchTermsData, existingKeywords, settings]);

  // Filtrer et trier les opportunités
  const filteredOpportunities = useMemo(() => {
    // Exclure les variantes proches - afficher uniquement les nouveaux mots-clés
    let result = analyzeOpportunities.filter(opp => opp.keywordStatus !== 'CLOSE_VARIANT');

    // Appliquer le filtre tier
    if (tierFilter) {
      if (tierFilter === 'HIGH_POTENTIAL') {
        result = result.filter(opp => opp.tier === 'EXCELLENT' || opp.tier === 'HIGH');
      } else {
        result = result.filter(opp => opp.tier === tierFilter);
      }
    }

    // Appliquer le tri
    const tierOrder = { 'EXCELLENT': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
    result = [...result].sort((a, b) => {
      let valA, valB;

      const statusOrder = { 'NEW_KEYWORD': 3, 'CLOSE_VARIANT': 2, 'EXACT_MATCH': 1 };

      switch (sortColumn) {
        case 'searchTerm':
          valA = a.searchTerm.toLowerCase();
          valB = b.searchTerm.toLowerCase();
          return sortDirection === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        case 'status':
          valA = statusOrder[a.keywordStatus] || 0;
          valB = statusOrder[b.keywordStatus] || 0;
          break;
        case 'tier':
          valA = tierOrder[a.tier] || 0;
          valB = tierOrder[b.tier] || 0;
          break;
        case 'impressions':
          valA = a.impressions;
          valB = b.impressions;
          break;
        case 'ctr':
          valA = a.ctr;
          valB = b.ctr;
          break;
        case 'clicks':
          valA = a.clicks;
          valB = b.clicks;
          break;
        case 'conversions':
          valA = a.conversions;
          valB = b.conversions;
          break;
        case 'cpa':
          valA = a.cpa || 0;
          valB = b.cpa || 0;
          break;
        default:
          valA = a.conversions;
          valB = b.conversions;
      }

      return sortDirection === 'asc' ? valA - valB : valB - valA;
    });

    return result;
  }, [analyzeOpportunities, tierFilter, sortColumn, sortDirection]);

  // Gestionnaire de tri
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Statistiques globales (excluant les variantes proches)
  const stats = useMemo(() => {
    // Exclure les variantes proches des stats
    const opportunities = analyzeOpportunities.filter(opp => opp.keywordStatus !== 'CLOSE_VARIANT');
    const total = opportunities.length;

    // Haut potentiel = EXCELLENT + HIGH
    const highPotential = opportunities.filter(opp => opp.tier === 'EXCELLENT' || opp.tier === 'HIGH').length;

    // Totaux clics et conversions
    const totalClicks = opportunities.reduce((sum, opp) => sum + opp.clicks, 0);
    const totalConversions = opportunities.reduce((sum, opp) => sum + opp.conversions, 0);

    return {
      total,
      highPotential,
      totalClicks,
      totalConversions
    };
  }, [analyzeOpportunities]);

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  // Fonction pour afficher les badges de type de campagne
  const getCampaignTypeBadges = (campaignTypes) => {
    // Gérer Array, Set ou undefined
    if (!campaignTypes) return null;

    // Convertir en array si c'est un Set
    const typesArray = campaignTypes instanceof Set
      ? Array.from(campaignTypes)
      : Array.isArray(campaignTypes)
        ? campaignTypes
        : [];

    if (typesArray.length === 0) return null;

    // Convertir les codes numériques en noms lisibles
    const typeMapping = {
      2: 'SEARCH',
      'SEARCH': 'SEARCH',
      4: 'SHOPPING',
      'SHOPPING': 'SHOPPING',
      10: 'PMAX',
      'PERFORMANCE_MAX': 'PMAX'
    };

    const styles = {
      SEARCH: { bg: '#dbeafe', color: '#1e40af' },
      SHOPPING: { bg: '#fef3c7', color: '#92400e' },
      PMAX: { bg: '#e0e7ff', color: '#3730a3' }
    };

    const uniqueTypes = [...new Set(typesArray.map(t => typeMapping[t]).filter(Boolean))];

    if (uniqueTypes.length === 0) return null;

    return (
      <span style={{ display: 'inline-flex', gap: '4px', marginLeft: '8px' }}>
        {uniqueTypes.map(type => (
          <span
            key={type}
            style={{
              display: 'inline-flex',
              padding: '2px 6px',
              fontSize: '10px',
              fontWeight: '500',
              borderRadius: '4px',
              backgroundColor: styles[type]?.bg || '#f3f4f6',
              color: styles[type]?.color || '#374151',
              textTransform: 'uppercase'
            }}
          >
            {type}
          </span>
        ))}
      </span>
    );
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div className="keyword-opportunities-tool" style={{ width: '100%', padding: '0' }}>
        {/* En-tête */}
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '24px', 
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ 
              fontSize: '24px', 
              fontWeight: '600', 
              color: '#111827', 
              display: 'flex', 
              alignItems: 'center',
              margin: '0 0 8px 0'
            }}>
              <Search style={{ marginRight: '8px', height: '24px', width: '24px', color: '#333333' }} />
              Détecteur d'Opportunités de Mots-Clés
            </h2>
            <p style={{ color: '#6b7280', margin: '0', fontSize: '16px' }}>
              Identifiez de nouveaux mots-clés performants basés sur vos termes de recherche
            </p>
          </div>
          {selectedAccount && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  border: hasCustomSettings && !showFilters ? '1px solid #16a34a' : '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: showFilters ? '#f3f4f6' : hasCustomSettings ? '#f0fdf4' : 'white',
                  color: hasCustomSettings && !showFilters ? '#166534' : '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <Settings style={{ height: '16px', width: '16px', marginRight: '8px' }} />
                {hasCustomSettings ? 'Paramètres personnalisés' : 'Paramètres'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sélecteur de compte */}
      {/* Sans compte, la carte du sélecteur n'a rien à encadrer : on affiche
          l'état vide seul, plutôt que de le loger dans un cadre dans un cadre. */}
      {!loadingAccounts && accounts.length === 0 ? (
        <AucunCompteGoogleAds token={token} />
      ) : (
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '20px', 
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '500', 
            color: '#111827',
            margin: '0 0 12px 0'
          }}>
            Compte Google Ads
          </h3>
        
          {loadingAccounts ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{ 
                display: 'inline-block',
                width: '20px',
                height: '20px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #333333',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ margin: '8px 0 0 0', color: '#6b7280' }}>Chargement des comptes...</p>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <label style={{ 
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                minWidth: '100px'
              }}>
                Sélectionner :
              </label>
              <select
                value={selectedAccount}
                onChange={(e) => {
                  setSelectedAccount(e.target.value);
                  // Réinitialiser les données et filtres lors du changement de compte
                  setSearchTermsData([]);
                  setExistingKeywords([]);
                  setTierFilter(null);
                  // Mettre à jour l'URL avec le compte sélectionné
                  if (updateUrlWithAccount) updateUrlWithAccount(e.target.value || null);
                }}
                style={{
                  flex: '1',
                  minWidth: '300px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value="">-- Choisir un compte --</option>
                {accounts.map(account => {
                  const accountId = account.sourceId || account.id;
                  const accountName = account.name || account.displayName;
                  // Éviter de dupliquer l'ID si déjà présent dans le nom
                  const displayName = accountName.includes(accountId) ? accountName : `${accountName} (${accountId})`;
                  return (
                    <option key={accountId} value={accountId}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Écran de chargement */}
      {selectedAccount && loading && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '60px 24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'inline-block',
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #333333',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 8px 0'
          }}>
            Analyse en cours...
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: '0'
          }}>
            Récupération des termes de recherche et analyse des opportunités
          </p>
        </div>
      )}

      {/* Statistiques - uniquement si un compte est sélectionné et pas en chargement */}
      {selectedAccount && !loading && (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              padding: '8px', 
              background: '#dbeafe', 
              borderRadius: '8px',
              marginRight: '16px'
            }}>
              <Target style={{ height: '24px', width: '24px', color: '#333333' }} />
            </div>
            <div>
              <p style={{ 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#6b7280',
                margin: '0 0 4px 0'
              }}>
                Opportunités détectées
              </p>
              <p style={{ 
                fontSize: '28px', 
                fontWeight: '600', 
                color: '#111827',
                margin: '0'
              }}>
                {formatNumber(stats.total)}
              </p>
            </div>
          </div>
        </div>

        <div 
          onClick={() => {
            // Basculer le filtre haut potentiel
            if (tierFilter === 'HIGH_POTENTIAL') {
              setTierFilter(null); // Désactiver le filtre
            } else {
              setTierFilter('HIGH_POTENTIAL'); // Activer le filtre
            }
          }}
          style={{ 
            background: tierFilter === 'HIGH_POTENTIAL' ? '#dcfce7' : 'white', 
            borderRadius: '12px', 
            padding: '24px',
            boxShadow: tierFilter === 'HIGH_POTENTIAL' ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: tierFilter === 'HIGH_POTENTIAL' ? '2px solid #16a34a' : '2px solid transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out'
          }}
          onMouseEnter={(e) => {
            if (tierFilter !== 'HIGH_POTENTIAL') {
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }
          }}
          onMouseLeave={(e) => {
            if (tierFilter !== 'HIGH_POTENTIAL') {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
            }
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              padding: '8px', 
              background: '#dcfce7', 
              borderRadius: '8px',
              marginRight: '16px'
            }}>
              <TrendingUp style={{ height: '24px', width: '24px', color: '#16a34a' }} />
            </div>
            <div>
              <p style={{ 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#6b7280',
                margin: '0 0 4px 0'
              }}>
                Haut potentiel {tierFilter === 'HIGH_POTENTIAL' && <span style={{ color: '#16a34a', fontSize: '12px' }}>(filtré)</span>}
              </p>
              <p style={{ 
                fontSize: '28px', 
                fontWeight: '600', 
                color: '#111827',
                margin: '0'
              }}>
                {formatNumber(stats.highPotential)}
              </p>
            </div>
          </div>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              padding: '8px',
              background: '#dbeafe',
              borderRadius: '8px',
              marginRight: '16px'
            }}>
              <MousePointer style={{ height: '24px', width: '24px', color: '#333333' }} />
            </div>
            <div>
              <p style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#6b7280',
                margin: '0 0 4px 0'
              }}>
                Clics
              </p>
              <p style={{
                fontSize: '28px',
                fontWeight: '600',
                color: '#111827',
                margin: '0'
              }}>
                {formatNumber(stats.totalClicks)}
              </p>
            </div>
          </div>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              padding: '8px',
              background: '#dcfce7',
              borderRadius: '8px',
              marginRight: '16px'
            }}>
              <Target style={{ height: '24px', width: '24px', color: '#16a34a' }} />
            </div>
            <div>
              <p style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#6b7280',
                margin: '0 0 4px 0'
              }}>
                Conversions
              </p>
              <p style={{
                fontSize: '28px',
                fontWeight: '600',
                color: '#111827',
                margin: '0'
              }}>
                {formatNumber(stats.totalConversions)}
              </p>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Panneau de filtres - masqué pendant le chargement */}
      {showFilters && !loading && (
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ 
            fontSize: '18px', 
            fontWeight: '500', 
            color: '#111827',
            margin: '0 0 16px 0'
          }}>
            Paramètres d'analyse
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '20px',
            marginBottom: '24px'
          }}>
            <div style={{ 
              minHeight: '70px',
              display: 'flex',
              flexDirection: 'column',
              width: '100%'
            }}>
              <label style={{ 
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Période d'analyse
              </label>
              <select
                value={settings.period}
                onChange={(e) => setSettings({...settings, period: e.target.value})}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="LAST_7_DAYS">7 derniers jours</option>
                <option value="LAST_30_DAYS">30 derniers jours</option>
                <option value="LAST_90_DAYS">90 derniers jours</option>
              </select>
            </div>

            <div style={{ 
              minHeight: '70px',
              display: 'flex',
              flexDirection: 'column',
              width: '100%'
            }}>
              <label style={{ 
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Type de campagne
              </label>
              <select
                value={settings.campaignType}
                onChange={(e) => setSettings({...settings, campaignType: e.target.value})}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="ALL">Toutes les campagnes</option>
                <option value="SEARCH">Search uniquement</option>
                <option value="SHOPPING">Shopping uniquement</option>
                <option value="PERFORMANCE_MAX">Performance Max uniquement</option>
              </select>
            </div>

            <div style={{ 
              minHeight: '70px',
              display: 'flex',
              flexDirection: 'column',
              width: '100%'
            }}>
              <label style={{ 
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Impressions minimales
              </label>
              <input
                type="number"
                value={settings.minImpressions}
                onChange={(e) => setSettings({...settings, minImpressions: parseInt(e.target.value) || 0})}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                min="0"
              />
            </div>

            <div style={{ 
              minHeight: '70px',
              display: 'flex',
              flexDirection: 'column',
              width: '100%'
            }}>
              <label style={{ 
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Clics minimaux
              </label>
              <input
                type="number"
                value={settings.minClicks}
                onChange={(e) => setSettings({...settings, minClicks: parseInt(e.target.value) || 0})}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                min="0"
              />
            </div>

            <div style={{
              minHeight: '70px',
              display: 'flex',
              flexDirection: 'column',
              width: '100%'
            }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Conversions minimales
              </label>
              <input
                type="number"
                step="1"
                value={settings.minConversions}
                onChange={(e) => setSettings({...settings, minConversions: parseInt(e.target.value) || 0})}
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                min="0"
              />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px' }}>
            <input
              type="checkbox"
              id="aggregateData"
              checked={settings.aggregateData}
              onChange={(e) => setSettings({ ...settings, aggregateData: e.target.checked })}
              style={{
                width: '18px',
                height: '18px',
                marginRight: '10px',
                cursor: 'pointer'
              }}
            />
            <label
              htmlFor="aggregateData"
              style={{ fontSize: '14px', fontWeight: '500', color: '#374151', cursor: 'pointer' }}
            >
              Agréger les données par terme
            </label>
          </div>

          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => { fetchData(); setShowFilters(false); }}
              style={{
                padding: '8px 16px',
                background: '#333333',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.target.style.background = '#1d4ed8'}
              onMouseLeave={(e) => e.target.style.background = '#333333'}
            >
              Sauvegarder les paramètres
            </button>
          </div>
        </div>
      )}

      {/* Tableau des opportunités - uniquement si un compte est sélectionné et pas en chargement */}
      {selectedAccount && !loading && (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        <div style={{ 
          padding: '24px', 
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ 
            fontSize: '18px', 
            fontWeight: '500', 
            color: '#111827',
            margin: '0'
          }}>
            Opportunités détectées ({formatNumber(filteredOpportunities.length)}{tierFilter ? ` / ${formatNumber(analyzeOpportunities.length)}` : ''})
          </h3>
          {tierFilter && (
            <button
              onClick={() => setTierFilter(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '6px 12px',
                background: '#f3f4f6',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
              onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
            >
              ✕ Réinitialiser le filtre
            </button>
          )}
        </div>

        {filteredOpportunities.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'separate',
              borderSpacing: '0'
            }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  <th
                    onClick={() => handleSort('searchTerm')}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: sortColumn === 'searchTerm' ? '#333333' : '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      userSelect: 'none',
                      minWidth: '200px'
                    }}>
                    Terme de recherche {sortColumn === 'searchTerm' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('tier')}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: sortColumn === 'tier' ? '#333333' : '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap'
                    }}>
                    Potentiel {sortColumn === 'tier' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('impressions')}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: sortColumn === 'impressions' ? '#333333' : '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap'
                    }}>
                    Impressions {sortColumn === 'impressions' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('ctr')}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: sortColumn === 'ctr' ? '#333333' : '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap'
                    }}>
                    CTR {sortColumn === 'ctr' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('clicks')}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: sortColumn === 'clicks' ? '#333333' : '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap'
                    }}>
                    Clics {sortColumn === 'clicks' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('cost')}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: sortColumn === 'cost' ? '#333333' : '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap'
                    }}>
                    Coût {sortColumn === 'cost' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('conversionRate')}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: sortColumn === 'conversionRate' ? '#333333' : '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap'
                    }}>
                    Taux conv. {sortColumn === 'conversionRate' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('conversions')}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: sortColumn === 'conversions' ? '#333333' : '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap'
                    }}>
                    Conv. {sortColumn === 'conversions' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('cpa')}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: sortColumn === 'cpa' ? '#333333' : '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      userSelect: 'none',
                      whiteSpace: 'nowrap'
                    }}>
                    CPA {sortColumn === 'cpa' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      whiteSpace: 'nowrap'
                    }}>
                    Valeur conv.
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      whiteSpace: 'nowrap'
                    }}>
                    COS
                  </th>
                  <th
                    style={{
                      padding: '12px 16px',
                      textAlign: 'center',
                      fontSize: '12px',
                      fontWeight: '500',
                      color: '#6b7280',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e5e7eb',
                      whiteSpace: 'nowrap'
                    }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: 'white' }}>
                {filteredOpportunities.map((opportunity, index) => (
                  <tr key={index} style={{
                    backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '500', color: '#111827', maxWidth: '350px', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      <span style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: '4px' }}>
                        <span
                          onClick={() => setSelectedTerm(opportunity)}
                          style={{
                            lineHeight: '1.5',
                            cursor: 'pointer',
                            color: '#333333',
                            textDecoration: 'underline',
                            textDecorationColor: 'transparent',
                            transition: 'text-decoration-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.textDecorationColor = '#333333'}
                          onMouseLeave={(e) => e.target.style.textDecorationColor = 'transparent'}
                        >
                          {opportunity.searchTerm}
                        </span>
                        {getCampaignTypeBadges(opportunity.campaignTypes)}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <span style={{
                        display: 'inline-flex',
                        padding: '4px 8px',
                        fontSize: '12px',
                        fontWeight: '600',
                        borderRadius: '9999px',
                        backgroundColor: opportunity.tier === 'EXCELLENT' ? '#dcfce7' : opportunity.tier === 'HIGH' ? '#dbeafe' : opportunity.tier === 'MEDIUM' ? '#fef3c7' : '#f3f4f6',
                        color: opportunity.tier === 'EXCELLENT' ? '#166534' : opportunity.tier === 'HIGH' ? '#1e40af' : opportunity.tier === 'MEDIUM' ? '#92400e' : '#374151'
                      }}>
                        {opportunity.tier === 'EXCELLENT' ? 'Excellent' :
                         opportunity.tier === 'HIGH' ? 'Haut' :
                         opportunity.tier === 'MEDIUM' ? 'Moyen' : 'Faible'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                      {formatNumber(opportunity.impressions)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                      {opportunity.ctr.toFixed(2)}%
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                      {formatNumber(opportunity.clicks)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(opportunity.cost || 0)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                      {opportunity.conversionRate?.toFixed(2) || '0.00'}%
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                      {opportunity.conversions % 1 === 0 ? opportunity.conversions : opportunity.conversions.toFixed(1)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                      {opportunity.cpa > 0 ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(opportunity.cpa) : '-'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(opportunity.conversionsValue || 0)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: '14px', color: '#6b7280' }}>
                      {opportunity.conversionsValue > 0 ? `${((opportunity.cost / opportunity.conversionsValue) * 100).toFixed(0)}%` : '-'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button
                        onClick={() => openAddKeywordModal(opportunity)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: 'white',
                          backgroundColor: '#16a34a',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#15803d'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#16a34a'}
                      >
                        <Plus size={14} />
                        Ajouter
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ 
            padding: '48px 24px', 
            textAlign: 'center'
          }}>
            <Search style={{ 
              margin: '0 auto 16px auto', 
              height: '48px', 
              width: '48px', 
              color: '#9ca3af'
            }} />
            <h3 style={{ 
              margin: '0 0 8px 0',
              fontSize: '16px',
              fontWeight: '500',
              color: '#111827'
            }}>
              Aucune opportunité détectée
            </h3>
            <p style={{ 
              margin: '0',
              fontSize: '14px',
              color: '#6b7280'
            }}>
              Essayez d'ajuster les filtres pour découvrir de nouvelles opportunités
            </p>
          </div>
        )}
      </div>
      )}

        {/* Modal de détails par campagne/adgroup */}
        {selectedTerm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => setSelectedTerm(null)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '900px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* En-tête du modal */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '20px'
              }}>
                <div>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                    Détails du terme de recherche
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#374151',
                    backgroundColor: '#f3f4f6',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    display: 'inline-block'
                  }}>
                    "{selectedTerm.searchTerm}"
                  </p>
                </div>
                <button
                  onClick={() => setSelectedTerm(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    color: '#6b7280'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Résumé global */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Impressions</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{formatNumber(selectedTerm.impressions)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Clics</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{formatNumber(selectedTerm.clicks)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Coût</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(selectedTerm.cost || 0)}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Conv.</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{selectedTerm.conversions}</div>
                </div>
              </div>

              {/* Tableau des campagnes/adgroups */}
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                Répartition par campagne / groupe d'annonces
              </h4>

              {selectedTerm.breakdown && selectedTerm.breakdown.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f9fafb' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Campagne
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Type
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Impr.
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Clics
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Coût
                        </th>
                        <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                          Conv.
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTerm.breakdown.map((row, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '10px 12px', fontSize: '13px', color: '#111827', maxWidth: '250px', wordBreak: 'break-word' }}>
                            {row.campaignName || '-'}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                            {getCampaignTypeBadges([row.campaignType])}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                            {formatNumber(row.impressions)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                            {formatNumber(row.clicks)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(row.cost || 0)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                            {row.conversions % 1 === 0 ? row.conversions : row.conversions.toFixed(1)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  Aucun détail de campagne disponible pour ce terme.
                </div>
              )}

              {/* Bouton fermer */}
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setSelectedTerm(null)}
                  style={{
                    padding: '8px 16px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                  onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal d'ajout de mot-clé */}
        {addKeywordTerm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={closeAddKeywordModal}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* En-tête du modal */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '20px'
              }}>
                <div>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                    Ajouter comme mot-clé
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#374151',
                    backgroundColor: '#dcfce7',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    display: 'inline-block'
                  }}>
                    {addKeywordTerm.searchTerm}
                  </p>
                </div>
                <button
                  onClick={closeAddKeywordModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    color: '#6b7280'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Message de succès */}
              {addSuccess && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#dcfce7',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Check size={20} style={{ color: '#16a34a' }} />
                  <span style={{ color: '#166534', fontSize: '14px' }}>{addSuccess}</span>
                </div>
              )}

              {/* Message d'erreur */}
              {addError && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertCircle size={20} style={{ color: '#dc2626' }} />
                  <span style={{ color: '#991b1b', fontSize: '14px' }}>{addError}</span>
                </div>
              )}

              {/* Type de correspondance */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Type de correspondance
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[
                    { value: 'EXACT', label: 'Exact', preview: `[${addKeywordTerm.searchTerm}]` },
                    { value: 'PHRASE', label: 'Expression', preview: `"${addKeywordTerm.searchTerm}"` },
                    { value: 'BROAD', label: 'Large', preview: addKeywordTerm.searchTerm }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setAddMatchType(type.value)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: addMatchType === type.value ? '2px solid #333333' : '1px solid #d1d5db',
                        borderRadius: '8px',
                        background: addMatchType === type.value ? '#FAF9F5' : 'white',
                        cursor: 'pointer',
                        textAlign: 'center'
                      }}
                    >
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {type.label}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        {type.preview}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Sélection des groupes d'annonces */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '12px'
                }}>
                  Groupes d'annonces cibles ({addSelectedAdGroups.length} sélectionné{addSelectedAdGroups.length > 1 ? 's' : ''})
                </label>

                {/* Section 1: Groupes d'annonces détectés */}
                {getDetectedAdGroups().length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#16a34a'
                      }}>
                        Groupes où le terme a été détecté
                      </span>
                      <span style={{
                        fontSize: '11px',
                        padding: '2px 8px',
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        borderRadius: '4px'
                      }}>
                        Recommandé
                      </span>
                    </div>
                    <div style={{
                      border: '2px solid #16a34a',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      {getDetectedAdGroups().map((ag, idx) => (
                        <div
                          key={ag.id || idx}
                          onClick={() => ag.id && toggleAdGroupSelection(ag.id)}
                          style={{
                            padding: '10px 12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: ag.id ? 'pointer' : 'default',
                            backgroundColor: addSelectedAdGroups.includes(ag.id) ? '#dcfce7' : 'white',
                            borderBottom: idx < getDetectedAdGroups().length - 1 ? '1px solid #e5e7eb' : 'none'
                          }}
                        >
                          {ag.id ? (
                            <input
                              type="checkbox"
                              checked={addSelectedAdGroups.includes(ag.id)}
                              onChange={() => {}}
                              style={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <span style={{ width: '16px' }}></span>
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{ag.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{ag.campaignName}</div>
                          </div>
                          {!ag.id && (
                            <span style={{
                              fontSize: '10px',
                              padding: '2px 6px',
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              borderRadius: '4px'
                            }}>
                              ID non disponible
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Section 2: Autres groupes d'annonces */}
                <div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#6b7280',
                    marginBottom: '8px'
                  }}>
                    {getDetectedAdGroups().length > 0 ? 'Autres groupes d\'annonces' : 'Groupes d\'annonces disponibles'}
                  </div>

                  {loadingAdGroups ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                      <div style={{
                        display: 'inline-block',
                        width: '20px',
                        height: '20px',
                        border: '2px solid #f3f3f3',
                        borderTop: '2px solid #333333',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <p style={{ margin: '8px 0 0 0', color: '#6b7280', fontSize: '14px' }}>Chargement des groupes d'annonces...</p>
                    </div>
                  ) : getOtherAdGroups().length === 0 ? (
                    <div style={{
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '14px'
                    }}>
                      {availableAdGroups.length === 0 ? 'Aucun groupe d\'annonces disponible' : 'Aucun autre groupe d\'annonces'}
                    </div>
                  ) : (
                    <div style={{
                      maxHeight: '200px',
                      overflowY: 'auto',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}>
                      {/* Regrouper par campagne */}
                      {Object.entries(
                        getOtherAdGroups().reduce((acc, ag) => {
                          const key = ag.campaignName || 'Sans campagne';
                          if (!acc[key]) acc[key] = [];
                          acc[key].push(ag);
                          return acc;
                        }, {})
                      ).map(([campaignName, adGroups]) => (
                        <div key={campaignName}>
                          <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#f9fafb',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6b7280',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            {campaignName}
                          </div>
                          {adGroups.map((ag) => (
                            <div
                              key={ag.id}
                              onClick={() => toggleAdGroupSelection(ag.id)}
                              style={{
                                padding: '10px 12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                backgroundColor: addSelectedAdGroups.includes(ag.id) ? '#FAF9F5' : 'white',
                                borderBottom: '1px solid #f3f4f6'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={addSelectedAdGroups.includes(ag.id)}
                                onChange={() => {}}
                                style={{ cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: '14px', color: '#111827' }}>{ag.name}</span>
                              {ag.status !== 'ENABLED' && (
                                <span style={{
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  backgroundColor: '#fef3c7',
                                  color: '#92400e',
                                  borderRadius: '4px'
                                }}>
                                  {ag.status}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Boutons d'action */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  onClick={closeAddKeywordModal}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                  onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
                >
                  Annuler
                </button>
                <button
                  onClick={handleAddKeyword}
                  disabled={addLoading || addSelectedAdGroups.length === 0 || addSuccess}
                  style={{
                    padding: '10px 20px',
                    background: addLoading || addSelectedAdGroups.length === 0 || addSuccess ? '#9ca3af' : '#16a34a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: addLoading || addSelectedAdGroups.length === 0 || addSuccess ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {addLoading ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Ajout en cours...
                    </>
                  ) : addSuccess ? (
                    <>
                      <Check size={16} />
                      Ajouté
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      Ajouter le mot-clé
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default KeywordOpportunitiesTool;